// Libraries
import React, { useState, useRef, useCallback } from 'react';

import { Box, Flex, useOutsideClick } from '@chakra-ui/react';
import CircularProgress from '@material-ui/core/CircularProgress';
import CharacterCount from '@tiptap/extension-character-count';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import sanitizeHtml from 'sanitize-html';
import TextGenerationComp from 'screens/performanceReview/components/TextGenerationComp';
import { useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

// Styles
import './RichTextEditor.css';
// Components
import RTEMenuBar from './components/RTEMenuBar';

const EMPTY_EDITOR_CHAR_COUNT = 4;

function RichTextEditor({
  placeholder = '',
  containerWidth = '100%',
  disabled = false,
  readOnly = false,
  maxCharLimit = null,
  onBlur = () => {},
  onChange = null,
  loading = false,
  initialText = '',
  plainText = '',
  showError = false,
  showToolbar = true,
  enableTextGeneration = false,
  textGenerationProps = {},
  autofocus = false,
  showCharacterCount = true,
  className = 'editor-container',
  providerType,
}) {
  const [charLimitError, setCharLimitError] = useState(false);
  const { isPreview } = useReviewPreviewParams();

  const [isFocused, setIsFocused] = useState(false);
  const editorContainerRef = useRef();

  // By default in nodeSize mode char count extension show 4 chars in empty state
  // so this is just to offset that
  const applicableMaxCharLimit = maxCharLimit ? maxCharLimit + EMPTY_EDITOR_CHAR_COUNT : maxCharLimit;

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          blockquote: false,
          bulletList: true,
          codeBlock: false,
          document: true,
          hardBreak: true,
          heading: false,
          horizontalRule: false,
          listItem: true,
          orderedList: true,
          paragraph: true,
          text: true,
          bold: true,
          code: false,
          italic: true,
          strike: true,
          dropcursor: false,
          gapcursor: false,
          history: true,
        }),
        Underline,
        Link.configure({
          HTMLAttributes: {
            class: 'rte-link',
          },
        }),
        Placeholder.configure({
          placeholder,
        }),
        CharacterCount.configure({
          // have set the limit to null but handling it manually
          // using this extension just to get character count
          limit: null,
          mode: 'nodeSize',
        }),
      ],
      autofocus,
      editable: !(disabled || readOnly),
      onUpdate({ editor: editor1 }) {
        if (editor1.storage.characterCount.characters() > applicableMaxCharLimit) {
          setCharLimitError(true);
        } else {
          setCharLimitError(false);
          const htmlText = sanitizeHtml(editor1.getHTML());
          const plainText1 = editor1.getText();
          if (onChange) {
            if (plainText1.trim() === '') {
              onChange('', '');
            } else {
              onChange(htmlText, plainText1);
            }
          }
        }
      },

      onCreate({ editor: editor1 }) {
        if (editor1.storage.characterCount.characters() > applicableMaxCharLimit) {
          setCharLimitError(true);
        } else {
          setCharLimitError(false);
        }
      },
      onFocus() {
        setIsFocused(true);
      },
      content: initialText || plainText,
    },
    [initialText, plainText]
  );

  const onClickOutside = useCallback(() => {
    // if (onChange) return;
    if (isFocused) {
      setIsFocused(false);

      if (!charLimitError) {
        const htmlText = sanitizeHtml(editor.getHTML());
        const plainText1 = editor.getText();
        if (plainText1.trim() === '') {
          onBlur('', '');
        } else {
          onBlur(htmlText, plainText1);
        }
      }
    }
  }, [editor, isFocused, onBlur, charLimitError]);

  useOutsideClick({
    ref: editorContainerRef,
    handler: onClickOutside,
  });

  if (!editor) {
    return null;
  }

  const numberOfCharacters = editor.storage.characterCount.characters();

  return (
    <>
      <Box pos="relative" maxW="600px">
        <Flex
          flexDir="column"
          ref={editorContainerRef}
          className={`${className} ${(showError || charLimitError) && !readOnly ? 'error' : ''} ${
            readOnly ? 'read-only' : ''
          }`}
          style={{
            width: containerWidth,
            fontSize: '14px',
          }}
          maxHeight="315px"
          position="relative"
          onClick={() => setIsFocused(true)}
          // pb="45px"
        >
          <Box overflowY="auto" flex={1}>
            <EditorContent editor={editor} />
          </Box>
          {showToolbar ? <RTEMenuBar editor={editor} /> : null}
        </Flex>
        {!readOnly && enableTextGeneration && (
          <Box position="absolute" bottom={{ base: '6px', desktop: '12px' }} right={{ base: '6px', desktop: '12px' }}>
            {/* for some reason character count is 4 when there is no text */}
            <TextGenerationComp
              providerType={providerType}
              isDisabled={isPreview || numberOfCharacters < 19}
              {...textGenerationProps}
            />
          </Box>
        )}
      </Box>

      {!readOnly ? (
        <Flex className="character-count" mt="4px" justifyContent="space-between" maxWidth="600px">
          {loading && <TextBodyMedium align="left">{loading ? <CircularProgress size={15} /> : ''}</TextBodyMedium>}
          {charLimitError && !readOnly ? (
            <TextBodyMedium color="functional.C62C02" align="left">
              Character limit exceeded, your changes will not be saved
            </TextBodyMedium>
          ) : null}
          {showCharacterCount && (
            <TextBodyMedium ml="auto" color={charLimitError ? 'functional.C62C02' : 'text.g3'}>
              {editor.storage.characterCount.characters() - EMPTY_EDITOR_CHAR_COUNT}/
              {applicableMaxCharLimit - EMPTY_EDITOR_CHAR_COUNT}
            </TextBodyMedium>
          )}
        </Flex>
      ) : null}
    </>
  );
}

export { RichTextEditor };
